<template>
  <transition name="toast-slide">
    <div v-if="visible" class="alert-toast" :class="`toast--${alert.type}`">

      <!-- Left accent bar -->
      <div class="toast-accent"></div>

      <!-- Body -->
      <div class="toast-body">
        <div class="toast-icon">
          <v-icon small>{{ typeConfig.icon }}</v-icon>
        </div>

        <div class="toast-content">
          <div class="toast-header">
            <span class="toast-type-label">{{ typeConfig.label }}</span>
            <v-btn icon x-small @click="dismiss" class="dismiss-btn">
              <v-icon x-small>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="toast-asset">{{ alert.asset }}</div>

          <div class="toast-meta">
            <span class="toast-chip">
              <v-icon x-small>mdi-map-marker-outline</v-icon>
              {{ alert.location }}
            </span>
            <span class="toast-chip">
              <v-icon x-small>mdi-speedometer</v-icon>
              {{ alert.speed }}
            </span>
            <span class="toast-chip">
              <v-icon x-small>mdi-clock-outline</v-icon>
              {{ formattedTime }}
            </span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="toast-actions">
        <v-btn
          x-small depressed
          class="detail-btn"
          @click="viewDetail"
        >
          <v-icon x-small left>mdi-arrow-right</v-icon>
          View event
        </v-btn>
        <v-btn x-small text class="dismiss-text-btn" @click="dismiss">
          Dismiss
        </v-btn>
      </div>

      <!-- Timer bar -->
      <div class="toast-timer">
        <div
          class="toast-timer-bar"
          :style="{ width: timerWidth + '%', transition: `width ${duration}ms linear` }"
        ></div>
      </div>

    </div>
  </transition>
</template>

<script>
const TYPE_CONFIG = {
  overspeed: { icon: 'mdi-gauge', label: 'Overspeed',      color: '#e24b4a' },
  stoppage:  { icon: 'mdi-clock-alert-outline', label: 'Long Stoppage', color: '#ef9f27' },
  geofence:  { icon: 'mdi-map-marker-alert-outline', label: 'Geofence Exit', color: '#1d9e75' },
  movement:  { icon: 'mdi-car-arrow-right', label: 'Movement',     color: '#185fa5' },
}

export default {
  name: 'AlertToast',

  props: {
    alert: {
      type: Object,
      required: true,
      // { id, type, asset, location, speed, eventId, timestamp }
    },
    duration: {
      type: Number,
      default: 30000, // 30 seconds
    },
  },

  data() {
    return {
      visible: false,
      timerWidth: 100,
      timer: null,
      startedAt: null,
    }
  },

  computed: {
    typeConfig() {
      return TYPE_CONFIG[this.alert.type] || TYPE_CONFIG.movement
    },
    formattedTime() {
      const d = this.alert.timestamp ? new Date(this.alert.timestamp) : new Date()
      return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    },
  },

  mounted() {
    // Trigger enter transition on next tick
    this.$nextTick(() => {
      this.visible = true
      this.startTimer()
    })
  },

  beforeDestroy() {
    clearTimeout(this.timer)
  },

  methods: {
    startTimer() {
      this.startedAt = Date.now()
      // Let CSS transition handle the visual bar shrink
      this.$nextTick(() => { this.timerWidth = 0 })
      this.timer = setTimeout(() => this.dismiss(), this.duration)
    },

    dismiss() {
      clearTimeout(this.timer)
      this.visible = false
      // Wait for leave transition then tell parent to remove
      setTimeout(() => this.$emit('dismissed', this.alert.id), 350)
    },

    viewDetail() {
      this.$emit('view-detail', this.alert)
      this.dismiss()
    },
  },
}
</script>

<style scoped>
/* ── Slide-in from right ── */
.toast-slide-enter-active { transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease; }
.toast-slide-leave-active  { transition: transform 0.25s ease, opacity 0.2s ease; }
.toast-slide-enter, .toast-slide-leave-to {
  transform: translateX(380px);
  opacity: 0;
}

.alert-toast {
  position: relative;
  width: 320px;
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid rgba(0,0,0,0.08);
  box-shadow: 0 4px 20px rgba(0,0,0,0.10);
  overflow: hidden;
  pointer-events: all;
}

/* Dark mode */
.theme--dark .alert-toast {
  background: #1e1e2e;
  border-color: rgba(255,255,255,0.08);
}

/* Accent bar */
.toast-accent {
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 3px;
}
.toast--overspeed .toast-accent { background: #e24b4a; }
.toast--stoppage  .toast-accent { background: #ef9f27; }
.toast--geofence  .toast-accent { background: #1d9e75; }
.toast--movement  .toast-accent { background: #185fa5; }

.toast-body {
  display: flex;
  gap: 10px;
  padding: 12px 12px 8px 18px;
  align-items: flex-start;
}

.toast-icon {
  width: 32px; height: 32px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.toast--overspeed .toast-icon { background: #fcebeb; color: #a32d2d; }
.toast--stoppage  .toast-icon { background: #faeeda; color: #854f0b; }
.toast--geofence  .toast-icon { background: #e1f5ee; color: #0f6e56; }
.toast--movement  .toast-icon { background: #e6f1fb; color: #0c447c; }

.theme--dark .toast--overspeed .toast-icon { background: #501313; color: #f09595; }
.theme--dark .toast--stoppage  .toast-icon { background: #412402; color: #fac775; }
.theme--dark .toast--geofence  .toast-icon { background: #04342c; color: #5dcaa5; }
.theme--dark .toast--movement  .toast-icon { background: #042c53; color: #85b7eb; }

.toast-content { flex: 1; min-width: 0; }

.toast-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
}

.toast-type-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.toast--overspeed .toast-type-label { color: #a32d2d; }
.toast--stoppage  .toast-type-label { color: #854f0b; }
.toast--geofence  .toast-type-label { color: #0f6e56; }
.toast--movement  .toast-type-label { color: #0c447c; }

.dismiss-btn { opacity: 0.4; }
.dismiss-btn:hover { opacity: 1; }

.toast-asset {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0,0,0,0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}
.theme--dark .toast-asset { color: rgba(255,255,255,0.87); }

.toast-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.toast-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: rgba(0,0,0,0.55);
}
.theme--dark .toast-chip { color: rgba(255,255,255,0.5); }
.toast-chip .v-icon { opacity: 0.7; }

.toast-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px 10px 18px;
}

.detail-btn {
  font-size: 12px !important;
  font-weight: 500 !important;
  border: 1px solid rgba(0,0,0,0.12) !important;
  background: transparent !important;
  color: rgba(0,0,0,0.75) !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}
.theme--dark .detail-btn {
  border-color: rgba(255,255,255,0.15) !important;
  color: rgba(255,255,255,0.75) !important;
}
.detail-btn:hover { background: rgba(0,0,0,0.04) !important; }

.dismiss-text-btn {
  font-size: 12px !important;
  color: rgba(0,0,0,0.4) !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}
.theme--dark .dismiss-text-btn { color: rgba(255,255,255,0.35) !important; }

/* Timer bar */
.toast-timer {
  height: 2px;
  background: rgba(0,0,0,0.06);
}
.theme--dark .toast-timer { background: rgba(255,255,255,0.06); }

.toast-timer-bar { height: 100%; }
.toast--overspeed .toast-timer-bar { background: #e24b4a; }
.toast--stoppage  .toast-timer-bar { background: #ef9f27; }
.toast--geofence  .toast-timer-bar { background: #1d9e75; }
.toast--movement  .toast-timer-bar { background: #185fa5; }
</style>